import React, { useEffect, useRef, useState } from 'react';
import { IEvent } from '../../interfaces';
import APIService, { IActiveCountry } from '../../services/APIService';
import rootStore, { IMobxRootState } from '../../RootStore';
import { C_DETAIL_VIEW_WAIT_FOR_ID_TIMEOUT_MS } from '../../constants';
import { getErrorMessage } from '../../utils/generalUtils';
import { inject } from 'mobx-react';
import {
    canDeleteScore,
    canEditScore,
    canScore,
} from 'components/eventsFeed/eventUtils';
import EventDisplay from './EventDisplay';
import { keyBy } from 'lodash/fp';

const NO_EVENT_MESSAGE = 'No event to display';

type Props = {
    licensedCountriesArr?: IActiveCountry[];
    id?: string;
};

export const EventsDisplayProvider = inject(
    ({ UserStore, scoreEventStore, countryStore }: IMobxRootState) => {
        return {
            canUserScore: canScore(scoreEventStore.activeEvent, UserStore),
            canUserEditScore: canEditScore(
                scoreEventStore.activeEvent,
                UserStore
            ),
            canUserDeleteScore: canDeleteScore(
                scoreEventStore.activeEvent,
                UserStore
            ),
            licensedCountriesArr: countryStore.countries.licensedCountriesArr,
        };
    }
)(({ licensedCountriesArr, id }: Props) => {
    const [isLoadingEvent, setIsLoadingEvent] = React.useState(true);
    const [eventData, setEventData] = useState<IEvent>(null);
    const [eventText, setEventText] = useState<string>(null);
    const [error, setError] = useState<string>(null);
    const eventDataRef = useRef(eventData);
    eventDataRef.current = eventData;
    useEffect(() => {
        if (id) return void loadEventData(id);
        if (eventDataRef.current) {
            setEventData(null);
            setEventText(null);
            setIsLoadingEvent(false);
            setError(NO_EVENT_MESSAGE);
            return;
        }
        const timer = setTimeout(() => {
            setIsLoadingEvent(false);
            setError(NO_EVENT_MESSAGE);
        }, C_DETAIL_VIEW_WAIT_FOR_ID_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [id]);
    const loadEventData = async (id: string) => {
        setIsLoadingEvent(true);
        setError(null);
        try {
            const event = await APIService.getScoreEvent(Number(id));
            setEventData(event);
            const eventText = await APIService.getEventContent(
                event.article_text
            );
            setEventText(eventText);
            rootStore.scoreEventStore.setActiveEvent(event);
        } catch (err) {
            console.log('load event error', err);
            setEventData(null);
            setEventText(null);
            setError(getErrorMessage(err, 'Failed to load event'));
        } finally {
            setIsLoadingEvent(false);
        }
    };
    const countriesById: { [countryID: number]: IActiveCountry } = keyBy(
        (country: IActiveCountry) => country.id,
        licensedCountriesArr
    );
    return (
        <EventDisplay
            eventData={eventData}
            eventText={eventText}
            isLoading={isLoadingEvent}
            error={error}
            countriesById={countriesById}
        />
    );
});

export default EventsDisplayProvider;
